// Default goAML <transaction_description> and <reason> per template, used when the row leaves
// Description / Reason empty. Shared so the server (server/utils/report_types/*) and the web grid
// (front/src/components/FiuGrid.vue, which pre-fills the cells) produce exactly the same text.
//
// `row` holds the row's values by column name as entered (e.g. 'Player Deposit', 'Deposit'),
// not the goAML codes the validators turn them into. A rule returns null when the row lacks what
// the text depends on (e.g. no / unknown Transfer Type), so callers can leave the cell empty.
const { FIUTemplatesTypes: T } = require('./index');

const text = (value) => (value === null || value === undefined ? '' : value.toString().trim());

const byTransferType = (texts) => (row) => texts[text(row['Transfer Type'])] || null;

const RULES = {
    [T.BANK_ACCOUNT_TRANSFER]: {
        // Description is mandatory for this template: no default
        Reason: () => 'Amount exceeds reporting Threshold',
    },
    [T.BANK_TO_BANK_INCOME_B2B]: {
        Reason: () => 'Amount exceeds reporting Threshold',
    },
    [T.BANK_MERCHANT_TRANSFER]: {
        Description: (row) => ({
            'Deposit': 'Funds sent to payment service provider for payouts',
            'Withdrawal': 'Funds received from payment service provider for account funding',
        })[text(row['Funds Type'])] || null,
        Reason: (row) => {
            const direction = { 'Deposit': 'to', 'Withdrawal': 'from' }[text(row['Funds Type'])];
            return direction ? `Amount transferred ${direction} Payment Service Provider exceeds threshold` : null;
        },
    },
    [T.DEPOSIT_VIA_PSP]: {
        Description: (row) => text(row['PSP Name'])
            ? `Player buys tokens in excess of threshold using ${text(row['PSP Name'])} account` : null,
        Reason: (row) => text(row['PSP Name'])
            ? `Buying of tokens on platform using ${text(row['PSP Name'])} account` : null,
    },
    [T.WIRE_TRANSFER_TRANSACTIONS]: {
        Description: byTransferType({
            'Player Deposit': 'Amount deposited by player on online gaming account exceeds threshold',
            'Player Withdrawal': 'Amount withdrawn by player from online gaming account exceeds threshold',
        }),
        Reason: byTransferType({
            'Player Deposit': 'Deposit on gaming account exceeds threshold',
            'Player Withdrawal': 'Withdrawal from gaming account exceeds threshold',
        }),
    },
    [T.CREDIT_CARD_TRANSACTIONS]: {
        Description: byTransferType({
            'Player Deposit': 'Transfer into player account from credit card in excess of threshold',
            'Player Withdrawal': 'Transfer out of player account to credit card in excess of threshold',
        }),
        Reason: byTransferType({
            'Player Deposit': 'Funding player platform through credit card exceeds the threshold amount',
            'Player Withdrawal': 'Payout to credit card of the player exceeds the threshold amount',
        }),
    },
    [T.WITHDRAWAL_VIA_PSP]: {
        Description: () => 'Amount withdrawn by player from online gaming account exceeds threshold',
        Reason: () => 'WIthdrawal from gaming account exceeding threshold',
    },
    [T.PAYOUT_TRANSACTION]: {
        Description: () => 'Payout of price money exceeds threshold',
        Reason: () => 'Amount exceeds treshold',
    },
};

// Columns that have a default for this template: ['Description', 'Reason'] or a subset
function defaultTextColumns(templateType) {
    return Object.keys(RULES[templateType] || {});
}

// The columns a template's defaults depend on (so the grid knows when to recompute)
const DEPENDS_ON = {
    [T.BANK_MERCHANT_TRANSFER]: ['Funds Type'],
    [T.DEPOSIT_VIA_PSP]: ['PSP Name'],
    [T.WIRE_TRANSFER_TRANSACTIONS]: ['Transfer Type'],
    [T.CREDIT_CARD_TRANSACTIONS]: ['Transfer Type'],
};
function defaultTextDependencies(templateType) {
    return DEPENDS_ON[templateType] || [];
}

// Default text for `column` ('Description' | 'Reason'), or null when there is none for this row
function defaultText(templateType, column, row) {
    const rule = RULES[templateType]?.[column];
    return rule ? rule(row || {}) : null;
}

module.exports = {
    defaultText,
    defaultTextColumns,
    defaultTextDependencies,
};
